
import { supabase } from '@/services/supabase/supabase';
import { useQuery } from '@tanstack/react-query';

export interface StoryMeta {
  id: string;
  projectId: string;
  name: string;
  plotline: string;
  genre?: string;
  status: 'draft' | 'in_progress' | 'completed';
  
  // Stats
  chapterCount: number;
  wordCount?: number;
  lastUpdated: string;
  
  // Navigation Context
  // currentChapterId might be better handled by a separate valid hook or URL parameter, 
  // but if stored in story metadata, can include here.
  
  isLoading: boolean;
  error: Error | null;
}

const STORY_META_KEY = (storyId: string) => ['meta', 'story', storyId];

export function useStoryMeta(storyId: string | null) {
  const { data, isLoading, error } = useQuery({
    queryKey: STORY_META_KEY(storyId || ''),
    queryFn: async () => {
      if (!storyId) return null;

      // Use the dashboard RPC which returns aggregated stats
      // Note: This RPC usually takes project_id, but here we might need a specific story fetch
      // If the RPC is get_story_dashboard_data(p_project_id), it returns the CURRENT story data.
      // If we need ANY story, we might need a different one. 
      // However, usually useStoryMeta is for the *active* story context.
      // Let's check if we can query by ID or if we should fallback to simple select if we want specific story.
      // For now, let's assume we want the specific story details.
      
      const { data: story, error: storyError } = await supabase
        .from('stories')
        .select('*')
        .eq('id', storyId)
        .single();
      
      if (storyError) throw storyError;

      // Optimize stats fetch - potentially separate or keep simple count
      // If we want to strictly follow the plan to use RPC, we'd need an RPC that takes story_id.
      // "get_story_dashboard_data" takes p_project_id.
      // Let's stick to the efficient select for single story if RPC is project-scoped, 
      // OR if we can filter the RPC result. 
      // But actually, for OVERVIEW page, we want the CURRENT story.
      
      const { count: chapterCount, error: countError } = await supabase
        .from('chapters')
        .select('*', { count: 'exact', head: true })
        .eq('story_id', storyId);

      return {
          id: story.id,
          projectId: story.project_id,
          name: story.name,
          plotline: story.plotline,
          genre: story.genre,
          status: story.status || 'draft',
          chapterCount: chapterCount || 0,
          wordCount: story.word_count || 0,
          lastUpdated: story.updated_at
      } as StoryMeta;
    },
    enabled: !!storyId,
    staleTime: 1000 * 60, // 1 minute
  });

  return {
    id: data?.id,
    projectId: data?.projectId,
    name: data?.name,
    plotline: data?.plotline,
    genre: data?.genre,
    status: data?.status,
    chapterCount: data?.chapterCount,
    wordCount: data?.wordCount,
    lastUpdated: data?.lastUpdated,
    isLoading,
    error: error as Error | null
  };
}
